import { INITIAL_BLOG_POSTS, type BlogPost } from "../data/blogs";
import { INITIAL_NEWS_ITEMS, type NewsItem } from "../data/news";

export interface FeatureFlags {
  enableLiveChat: boolean;
  enableEvents: boolean;
  enableTestimonials: boolean;
  enableCareers: boolean;
  enablePricingCalculator: boolean;
  enableBlog: boolean;
  enableNews: boolean;
  enableAnnouncementBar: boolean;
  maintenanceMode: boolean;
}

export interface PageStatus {
  path: string;
  name: string;
  status: "published" | "draft";
  lastUpdated: string;
}

export interface StoredLead {
  id: string;
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  timestamp: string;
  status: "new" | "reviewed" | "archived";
}

export interface SiteContent {
  heroTagline: string;
  heroSubtext: string;
  contactPhone: string;
  contactEmail: string;
  officeAddress: string;
}

export interface TestimonialItem {
  id: string;
  name: string;
  role: string;
  organization: string;
  sector: string;
  rating: number;
  content: string;
  status: "pending" | "approved" | "rejected";
  timestamp: string;
}

export interface EventItem {
  id: string;
  title: string;
  category: string;
  date: string;
  time: string;
  venue: string;
  seats: string;
  status: "Registration Open" | "Filling Fast" | "Completed";
}

export interface KpiItem {
  id: string;
  metric: string;
  label: string;
  subtext: string;
}

export interface CaseStudyItem {
  id: string;
  title: string;
  category: string;
  summary: string;
  impact: string;
}

export type { BlogPost, NewsItem };

const DEFAULT_FLAGS: FeatureFlags = {
  enableLiveChat: true,
  enableEvents: true,
  enableTestimonials: false, // Disabled by default until verified client feedback is received
  enableCareers: true,
  enablePricingCalculator: true,
  enableBlog: true,
  enableNews: true,
  enableAnnouncementBar: true,
  maintenanceMode: false,
};

const DEFAULT_PAGES: PageStatus[] = [
  { path: "/", name: "Homepage", status: "published", lastUpdated: "2026-08-17" },
  { path: "/services", name: "Services & Capabilities", status: "published", lastUpdated: "2026-08-17" },
  { path: "/courses", name: "Courses & Training", status: "published", lastUpdated: "2026-08-17" },
  { path: "/blog", name: "Technical Blog", status: "published", lastUpdated: "2026-08-17" },
  { path: "/news", name: "News & Dispatches", status: "published", lastUpdated: "2026-08-17" },
  { path: "/pricing", name: "Pricing Schedule", status: "published", lastUpdated: "2026-08-17" },
  { path: "/careers", name: "Careers & Jobs", status: "published", lastUpdated: "2026-08-17" },
  { path: "/about", name: "About & Governance", status: "published", lastUpdated: "2026-08-17" },
  { path: "/contact", name: "Contact & Desk", status: "published", lastUpdated: "2026-08-17" },
];

const DEFAULT_LEADS: StoredLead[] = [];

const DEFAULT_CONTENT: SiteContent = {
  heroTagline: "Engineering practice delivering high-performance software platforms, embedded hardware, robotics, security systems, and technical education.",
  heroSubtext: "Research And Development Infrastructure for Electronic and Computing Equipment.",
  contactPhone: "+91 95675 51437",
  contactEmail: "connect@radiece.com",
  officeAddress: "Building No. 24/1701, Suite No. 1034, Heiley Offices, 2nd Floor KC Arcade, Near TV Centre, CSEZ P.O, Kakkanad, Ernakulam - 682037, Kerala, India",
};

const DEFAULT_TESTIMONIALS: TestimonialItem[] = [];

const DEFAULT_EVENTS: EventItem[] = [
  {
    id: "EVT-201",
    title: "Enterprise Cloud Architecture & Distributed Systems Briefing",
    category: "Software Engineering",
    date: "Quarterly Session (Inquire for Schedule)",
    time: "10:00 AM - 01:00 PM IST",
    venue: "Kakkanad Corporate Campus & Virtual",
    seats: "Registration Open",
    status: "Registration Open",
  },
  {
    id: "EVT-202",
    title: "Industrial IoT Prototyping & Embedded Microcontroller Symposia",
    category: "Hardware & IoT",
    date: "Quarterly Session (Inquire for Schedule)",
    time: "02:00 PM - 05:00 PM IST",
    venue: "Kakkanad Hardware R&D Lab",
    seats: "Registration Open",
    status: "Registration Open",
  },
];

const DEFAULT_KPIS: KpiItem[] = [
  {
    id: "kpi-1",
    metric: "100%",
    label: "IP & Source Ownership",
    subtext: "Complete client ownership upon project milestone completion",
  },
  {
    id: "kpi-2",
    metric: "24h",
    label: "Engineering SLA",
    subtext: "Technical inquiry & architectural response guarantee",
  },
  {
    id: "kpi-3",
    metric: "865 MHz",
    label: "ISM Telemetry Standard",
    subtext: "Full compliance with Indian industrial wireless spectrum",
  },
  {
    id: "kpi-4",
    metric: "Q4 2026",
    label: "RaBuOS Public Beta",
    subtext: "Targeted public release for enterprise operating platform",
  },
];

const DEFAULT_CASE_STUDIES: CaseStudyItem[] = [
  {
    id: "BP-101",
    title: "RaBuOS Enterprise Business Operating System (In Development)",
    category: "Enterprise Cloud SaaS",
    summary: "Modular architecture engineered to unify MCA statutory governance, automated GST schedules, and visual task DAG workflows on Bun and TypeScript.",
    impact: "Public Beta targeted for Q4 2026 (~Dec)",
  },
  {
    id: "BP-102",
    title: "Industrial LoRaWAN Wireless Telemetry Mesh Blueprint",
    category: "Hardware & IoT",
    summary: "Reference hardware architecture utilizing ESP32-S3 and Semtech SX1262 for long-range 10km+ sensor telemetry with deep-sleep power gating.",
    impact: "Sub-watt low-power operational profile",
  },
  {
    id: "BP-103",
    title: "DPDP Act 2023 Zero-Trust Data Architecture Blueprint",
    category: "Cyber Security & Governance",
    summary: "Zero-trust reference schema with tokenized PII, field-level AES-256 encryption, and automated cryptographic audit logging aligned with Indian statutory mandates.",
    impact: "Strict statutory compliance & CERT-In alignment",
  },
];

const DEFAULT_ANNOUNCEMENT = "🚀 RaBuOS Platform in active development — Public Beta scheduled for Q4 2026 (~Dec). Explore Roadmap & Updates →";

const STORAGE_KEYS = {
  FLAGS: "radiece_admin_feature_flags",
  PAGES: "radiece_admin_page_statuses",
  LEADS: "radiece_admin_leads",
  ANNOUNCEMENT: "radiece_admin_announcement",
  CONTENT: "radiece_admin_site_content",
  TESTIMONIALS: "radiece_admin_testimonials",
  EVENTS: "radiece_admin_events",
  KPIS: "radiece_admin_kpis",
  CASE_STUDIES: "radiece_admin_case_studies",
  BLOGS: "radiece_admin_blogs",
  NEWS: "radiece_admin_news",
};

export function getFeatureFlags(): FeatureFlags {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.FLAGS);
    return raw ? { ...DEFAULT_FLAGS, ...JSON.parse(raw) } : DEFAULT_FLAGS;
  } catch {
    return DEFAULT_FLAGS;
  }
}

export function saveFeatureFlags(flags: FeatureFlags): void {
  localStorage.setItem(STORAGE_KEYS.FLAGS, JSON.stringify(flags));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getPageStatuses(): PageStatus[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.PAGES);
    return raw ? JSON.parse(raw) : DEFAULT_PAGES;
  } catch {
    return DEFAULT_PAGES;
  }
}

export function savePageStatuses(pages: PageStatus[]): void {
  localStorage.setItem(STORAGE_KEYS.PAGES, JSON.stringify(pages));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getStoredLeads(): StoredLead[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.LEADS);
    return raw ? JSON.parse(raw) : DEFAULT_LEADS;
  } catch {
    return DEFAULT_LEADS;
  }
}

export function addStoredLead(lead: Omit<StoredLead, "id" | "timestamp" | "status">): StoredLead {
  const current = getStoredLeads();
  const newLead: StoredLead = {
    ...lead,
    id: `LEAD-${Math.floor(100000 + Math.random() * 900000)}`,
    timestamp: new Date().toISOString().replace("T", " ").slice(0, 16),
    status: "new",
  };
  const updated = [newLead, ...current];
  localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
  return newLead;
}

export function updateLeadStatus(id: string, status: StoredLead["status"]): void {
  const current = getStoredLeads();
  const updated = current.map((l) => (l.id === id ? { ...l, status } : l));
  localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getAnnouncement(): string {
  try {
    return localStorage.getItem(STORAGE_KEYS.ANNOUNCEMENT) ?? DEFAULT_ANNOUNCEMENT;
  } catch {
    return DEFAULT_ANNOUNCEMENT;
  }
}

export function saveAnnouncement(text: string): void {
  localStorage.setItem(STORAGE_KEYS.ANNOUNCEMENT, text);
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getSiteContent(): SiteContent {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CONTENT);
    return raw ? { ...DEFAULT_CONTENT, ...JSON.parse(raw) } : DEFAULT_CONTENT;
  } catch {
    return DEFAULT_CONTENT;
  }
}

export function saveSiteContent(content: SiteContent): void {
  localStorage.setItem(STORAGE_KEYS.CONTENT, JSON.stringify(content));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getTestimonials(): TestimonialItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.TESTIMONIALS);
    return raw ? JSON.parse(raw) : DEFAULT_TESTIMONIALS;
  } catch {
    return DEFAULT_TESTIMONIALS;
  }
}

export function submitTestimonial(t: Omit<TestimonialItem, "id" | "status" | "timestamp">): TestimonialItem {
  const current = getTestimonials();
  const newItem: TestimonialItem = {
    ...t,
    id: `TEST-${Math.floor(1000 + Math.random() * 9000)}`,
    status: "pending",
    timestamp: new Date().toISOString().split("T")[0]!,
  };
  const updated = [newItem, ...current];
  localStorage.setItem(STORAGE_KEYS.TESTIMONIALS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
  return newItem;
}

export function updateTestimonialStatus(id: string, status: TestimonialItem["status"]): void {
  const current = getTestimonials();
  const updated = current.map((item) => (item.id === id ? { ...item, status } : item));
  localStorage.setItem(STORAGE_KEYS.TESTIMONIALS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function deleteTestimonial(id: string): void {
  const current = getTestimonials();
  const updated = current.filter((item) => item.id !== id);
  localStorage.setItem(STORAGE_KEYS.TESTIMONIALS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getEvents(): EventItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.EVENTS);
    return raw ? JSON.parse(raw) : DEFAULT_EVENTS;
  } catch {
    return DEFAULT_EVENTS;
  }
}

export function saveEvents(events: EventItem[]): void {
  localStorage.setItem(STORAGE_KEYS.EVENTS, JSON.stringify(events));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getKpis(): KpiItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.KPIS);
    return raw ? JSON.parse(raw) : DEFAULT_KPIS;
  } catch {
    return DEFAULT_KPIS;
  }
}

export function saveKpis(kpis: KpiItem[]): void {
  localStorage.setItem(STORAGE_KEYS.KPIS, JSON.stringify(kpis));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function getCaseStudies(): CaseStudyItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.CASE_STUDIES);
    return raw ? JSON.parse(raw) : DEFAULT_CASE_STUDIES;
  } catch {
    return DEFAULT_CASE_STUDIES;
  }
}

export function saveCaseStudies(cs: CaseStudyItem[]): void {
  localStorage.setItem(STORAGE_KEYS.CASE_STUDIES, JSON.stringify(cs));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

// Blog Management
export function getBlogPosts(): BlogPost[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.BLOGS);
    return raw ? JSON.parse(raw) : INITIAL_BLOG_POSTS;
  } catch {
    return INITIAL_BLOG_POSTS;
  }
}

export function saveBlogPost(post: BlogPost): void {
  const current = getBlogPosts();
  const exists = current.some((p) => p.slug === post.slug);
  const updated = exists ? current.map((p) => (p.slug === post.slug ? post : p)) : [post, ...current];
  localStorage.setItem(STORAGE_KEYS.BLOGS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function deleteBlogPost(slug: string): void {
  const current = getBlogPosts();
  const updated = current.filter((p) => p.slug !== slug);
  localStorage.setItem(STORAGE_KEYS.BLOGS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

// News Management
export function getNewsItems(): NewsItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.NEWS);
    return raw ? JSON.parse(raw) : INITIAL_NEWS_ITEMS;
  } catch {
    return INITIAL_NEWS_ITEMS;
  }
}

export function saveNewsItem(item: NewsItem): void {
  const current = getNewsItems();
  const exists = current.some((n) => n.id === item.id || n.slug === item.slug);
  const updated = exists
    ? current.map((n) => (n.id === item.id || n.slug === item.slug ? item : n))
    : [item, ...current];
  localStorage.setItem(STORAGE_KEYS.NEWS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
}

export function deleteNewsItem(id: string): void {
  const current = getNewsItems();
  const updated = current.filter((n) => n.id !== id);
  localStorage.setItem(STORAGE_KEYS.NEWS, JSON.stringify(updated));
  window.dispatchEvent(new Event("radiece_admin_update"));
}
